/**
 * Product Form Components
 *
 */

// Show gallery immediately - run as soon as script loads
(function() {
  function showGallery() {
    const imageGallery = document.querySelector('.product-images-wrapper');
    const productContainer = document.querySelector('[data-product-container]');
    const isSingleVariant = productContainer && productContainer.closest('.main-product-container').classList.contains('only-default-variant');
    
    // For single-variant products, don't show gallery immediately - let the metafield logic handle it
    if (isSingleVariant) {
      return;
    }
    
    if(imageGallery) {
      imageGallery.classList.add('loader-ready');
    }
  }

  // Try to show immediately if DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', showGallery);
  } else {
    showGallery();
  }

  // Fallback: try again after a short delay to catch edge cases
  setTimeout(showGallery, 10);
})();
class ProductForm extends HTMLElement {
  constructor() {
    super();

    this.form = this.querySelector('form');
    this.qtyBtns = this.querySelectorAll('[data-qty-btn]');
    this.cartElement = document.querySelector('cart-form');   
    this.variantMetafieldElement = this.form ? this.form.querySelector('[data-variantJSON-metafields]') : null;
    this.defaultUpsellMultiplier = 3;
    this.moneyFormat = window?.globalVariables?.money_format;
    this.productContainer = this.closest('[data-product-container]') || document;
    this.submitButton = null;
    this.qtyInputField = null;
    this.upsellManager = null;
  }

  connectedCallback() {
    this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
    if(this.querySelector('[data-qty-container]')){
      this.qtyBtns.forEach(qtyBtn => qtyBtn.addEventListener('click', this.manageQtyBtn.bind(this)));
    }

    if (window.BubsUpsellModalManager) {
      const modalEl = this.productContainer?.querySelector('[data-upsell-modal]') || document.querySelector('[data-upsell-modal]');
      this.upsellManager = new window.BubsUpsellModalManager({
        productForm: this,
        formElement: this.form,
        modalElement: modalEl,
        variantMetafieldJSON: this.variantMetafieldElement ? this.variantMetafieldElement.textContent : null,
        defaultMultiplier: this.defaultUpsellMultiplier,
        moneyFormat: this.moneyFormat,
        productId: this.form?.dataset?.productId,
        productTitle: window?.globalVariables?.product?.title || document?.querySelector('[data-product-title]')?.textContent?.trim(),
        currency: window?.Shopify?.currency?.active || window?.globalVariables?.currency || 'USD',
        modalVariant: 'default',
        onSubmit: (payload) => this.sendAddToCartRequest(payload)
      });
    }
  }

  /**
   * Product Form Submit event
   *
   * @param {evt} Event instance
   */
  onSubmitHandler(evt) {
    evt.preventDefault();
    const submitButton = this.querySelector('[type="submit"]');
    const qtyInput = this.querySelector('[data-qty-input]');
    this.submitButton = submitButton;
    this.qtyInputField = qtyInput;

    let itemPayload = {};
    try {
      itemPayload = JSON.parse(Utility.serializeForm(this.form));
    } catch (error) {
      console.error('[Product form] Unable to serialize form for submission', error);
      if (window.notificationEle) {
        window.notificationEle.updateNotification('Add to Cart', 'Something went wrong. Please try again.', { type: 'error', timeout: 5000 });
      }
      return;
    }

    // Ensure one-time purchases do not send a selling_plan.
    // quantity-block format has no [data-purchase_type] element — it uses an empty string
    // on the hidden selling_plan input for one-time and a plan ID for subscriptions.
    // Applying the recharge-widget detection logic to quantity-block always resolves to
    // 'onetime' and would incorrectly strip the selling_plan from subscription payloads.
    const format = this.form.dataset.format;
    if (format !== 'quantity-block') {
      const purchaseType = this.form
        .querySelector('[data-purchase_type] input:checked')
        ?.closest('[data-purchase_type]')
        ?.getAttribute('data-purchase_type') || 'onetime';
      if (purchaseType === 'onetime') {
        delete itemPayload.selling_plan;
        delete itemPayload.selling_plan_group;
      }
    }
    // Always remove selling_plan if it was left empty (one-time in quantity-block format)
    if (itemPayload.selling_plan === '' || itemPayload.selling_plan === null) {
      delete itemPayload.selling_plan;
    }

    if (this.upsellManager && this.upsellManager.handleSubmit(itemPayload)) {
      return;
    }

    this.sendAddToCartRequest(itemPayload);
  }
  setLoadingState(isLoading) {
    const submitButton = this.submitButton || this.querySelector('[type="submit"]');
    if (submitButton) {
      if (isLoading) {
        submitButton.classList.add('loading');
        submitButton.setAttribute('disabled', true);
      } else {
        submitButton.classList.remove('loading');
        submitButton.removeAttribute('disabled');
      }
    }

    document.querySelectorAll('[data-atc-out]')?.forEach(ele => {
      if (isLoading) {
        ele.classList.add('loading');
        ele.setAttribute('aria-disabled', 'true');
        ele.setAttribute('disabled', 'disabled');
      } else {
        ele.classList.remove('loading');
        ele.removeAttribute('disabled');
        ele.setAttribute('aria-disabled', 'false');
      }
    });
  }

  getSectionsToRender() {
    const template = window.globalVariables?.template;
    return `${(template == 'cart') ? 'template-cart' : 'component-cart-drawer'},header`;
  }

  sendAddToCartRequest(payload) {
    if (!payload) return;

    const addItems = [payload];
    const body = JSON.stringify({
      items: addItems,
      sections: this.getSectionsToRender()
    });

    this.setLoadingState(true);

    const cartAddUrl = window.routes?.cart_add_url || '/cart/add.js';
    const normalizedCartAddUrl = cartAddUrl.endsWith('.js') ? cartAddUrl : `${cartAddUrl}.js`;

    fetch(normalizedCartAddUrl, { ...Utility.fetchConfig(), body })
      .then((response) => {
        if (!response.ok) {
          return response.text().then((text) => {
            const isLocalPreview = /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);
            const message = response.status === 502 && isLocalPreview
              ? "Add to cart isn't working in local preview (502). Try again or test on your live store."
              : (text || `Add to cart failed (${response.status})`);
            throw new Error(message);
          });
        }
        return response.json().catch(() => {
          throw new Error('Invalid response from server. Please try again.');
        });
      })
      .then((res) => {
        let dataJSON = ((typeof res == 'object') ? res : JSON.parse(res));
        if(dataJSON.errors){
          console.error('[Product form] Add to cart error', dataJSON.errors);
          window.notificationEle?.updateNotification('Add to Cart', dataJSON.errors, {
            type: 'error',
            timeout: 5000
          });
          return;
        }

        if(this.qtyInputField) this.qtyInputField.value = 1;

        // Dispatch cart update events so RC overlay/cart listeners can react
        fetch('/cart.js')
          .then((cartRes) => (cartRes.ok ? cartRes.json() : Promise.reject(new Error('Cart unavailable'))))
          .then((cart) => {
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
            document.dispatchEvent(new CustomEvent('recharge:cart:updated', { detail: cart }));
          })
          .catch(() => {
            // Ignore cart fetch errors for debug events
          });


        if (this.cartElement && typeof this.cartElement._updateCart === 'function') {
          this.cartElement._updateCart(dataJSON.sections, 'open_drawer');
        } else if (this.cartElement && typeof this.cartElement.getCartData === 'function') {
          this.cartElement.getCartData('open_drawer');
        }      
      })
      .catch((e) => {
        console.error('[Product form] Add to cart request failed', e);
        const message = (e && e.message) ? e.message : 'Something went wrong. Please try again.';
        window.notificationEle?.updateNotification('Error', message, {
          type: 'error',
          timeout: 5000
        });
      })
      .finally(() => {
        this.setLoadingState(false);
      });
  }


  /**
   * Product Form Quantity Input update action
   *
   * @param {event} Event instance
   */
  manageQtyBtn(event) {
    event.preventDefault();
    let currentTarget = event.currentTarget;
    let action = currentTarget.dataset.for || 'increase';
    let $qtyInput = currentTarget.closest('[data-qty-container]').querySelector('[data-qty-input]');
    let currentQty = parseInt($qtyInput.value) || 1;
    let finalQty = 1;
    let decreaseQtyBtn = currentTarget.closest('[data-qty-container]').querySelector('[data-for="decrease"]');

    if(action == 'decrease' && currentQty <= 1){
      if(decreaseQtyBtn) decreaseQtyBtn.classList.add('disabled');
      return false;
    } else if(action == 'decrease'){
      finalQty = currentQty - 1;
      finalQty == 1 ? decreaseQtyBtn.classList.add('disabled') : decreaseQtyBtn.classList.remove('disabled');
    } else {
      if(decreaseQtyBtn) decreaseQtyBtn.classList.remove('disabled');
      finalQty = currentQty + 1;
    }

    $qtyInput.value = finalQty;
  }
}
customElements.define('product-form', ProductForm);
